import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * @typedef {"pending"|"active"|"done"|"error"} StepStatus
 * @typedef {{ label: string, status: StepStatus }} AdminJobStep
 * @typedef {{
 *   id: string;
 *   kind: string;
 *   title: string;
 *   subtitle?: string;
 *   status: "running"|"success"|"error";
 *   steps: AdminJobStep[];
 *   error?: string;
 *   startedAt: number;
 * }} AdminJob
 */

const AdminJobsContext = createContext(null);

function makeId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `job_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function AdminJobsProvider({ children }) {
  const [jobs, setJobs] = useState(/** @type {AdminJob[]} */ ([]));

  const patchJob = useCallback((id, patch) => {
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...patch } : j)));
  }, []);

  const setStepStatuses = useCallback((id, activeIndex, { errorIndex } = {}) => {
    setJobs((prev) =>
      prev.map((j) => {
        if (j.id !== id) return j;
        const steps = j.steps.map((s, si) => {
          if (typeof errorIndex === "number" && si === errorIndex) return { ...s, status: "error" };
          if (si < activeIndex) return { ...s, status: "done" };
          if (si === activeIndex) return { ...s, status: "active" };
          return { ...s, status: "pending" };
        });
        return { ...j, steps };
      })
    );
  }, []);

  const dismissJob = useCallback((id) => {
    setJobs((prev) => prev.filter((j) => j.id !== id));
  }, []);

  const clearFinished = useCallback(() => {
    setJobs((prev) => prev.filter((j) => j.status === "running"));
  }, []);

  /**
   * @param {{ kind?: string; title: string; stepLabels: string[] }} def
   * @returns {{ id: string; setActiveStep: (i: number) => void; setSubtitle: (s: string) => void; complete: () => void; fail: (err: unknown) => void }}
   */
  const beginJob = useCallback(
    (def) => {
      const id = makeId();
      const job = {
        id,
        kind: def.kind || "task",
        title: def.title,
        subtitle: "",
        status: "running",
        steps: (def.stepLabels || []).map((label) => ({ label, status: /** @type {StepStatus} */ ("pending") })),
        startedAt: Date.now(),
      };
      setJobs((prev) => [...prev, job]);
      return {
        id,
        setActiveStep: (i) => setStepStatuses(id, i),
        setSubtitle: (s) => patchJob(id, { subtitle: s }),
        complete: () => {
          setJobs((prev) =>
            prev.map((j) =>
              j.id !== id
                ? j
                : {
                    ...j,
                    status: "success",
                    subtitle: "",
                    steps: j.steps.map((s) => ({ ...s, status: s.status === "error" ? "error" : "done" })),
                  }
            )
          );
        },
        fail: (err) => {
          const msg = err instanceof Error ? err.message : String(err || "Lỗi");
          setJobs((prev) =>
            prev.map((j) =>
              j.id !== id
                ? j
                : {
                    ...j,
                    status: "error",
                    error: msg,
                    steps: j.steps.map((s) => (s.status === "active" ? { ...s, status: "error" } : s)),
                  }
            )
          );
        },
      };
    },
    [patchJob, setStepStatuses]
  );

  const hasRunningJob = useMemo(() => jobs.some((j) => j.status === "running"), [jobs]);

  const value = useMemo(
    () => ({
      jobs,
      beginJob,
      dismissJob,
      clearFinished,
      hasRunningJob,
    }),
    [jobs, beginJob, dismissJob, clearFinished, hasRunningJob]
  );

  return <AdminJobsContext.Provider value={value}>{children}</AdminJobsContext.Provider>;
}

export function useAdminJobs() {
  const ctx = useContext(AdminJobsContext);
  if (!ctx) throw new Error("useAdminJobs must be used within AdminJobsProvider");
  return ctx;
}
